import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  TextField,
  createFilterOptions,
} from '@mui/material';
import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { MAIN_THEME } from '@styles';
import FormFieldWrapper from './FormFieldWrapper';
import { IFormFieldInputBaseProps } from '.';
import { GlobalLovInjector } from '@redux/lovs/types';
import { useAppSelector } from '@redux/hooks';
import { IAbstractRecord } from '@models';

interface IMultiselectFormFieldBase extends IFormFieldInputBaseProps {
  values: string[];
  hideError?: boolean;
  loader?: boolean;
  maxTagCount?: number;
  freeSolo?: boolean;
  onBlurSaveNewData?: boolean;
  onChange?: (value: string[]) => void;
  onBlur?: (extraValue: string[]) => void;
  onFocus?: () => void;
  classes?: { input?: string; wrapper?: string };
}

// 1) If `injectGlobalLov` is not provided, `selectOptions` is required.
interface IMultiselectFormFieldWithLocalOptions
  extends IMultiselectFormFieldBase {
  selectOptions: Record<string, string>;
  injectGlobalLov?: never;
}

// 2) If `injectGlobalLov` is provided, `selectOptions` is not allowed.
interface IMultiselectFormFieldWithGlobalLov extends IMultiselectFormFieldBase {
  injectGlobalLov: GlobalLovInjector;
  selectOptions?: never;
}

export type IMultiselectFormFieldProps =
  | IMultiselectFormFieldWithLocalOptions
  | IMultiselectFormFieldWithGlobalLov;

const useStyles = makeStyles<{
  error?: string;
  material?: boolean;
}>()((theme, { error }) => ({
  inputContainerStyles: {
    width: '100%',
    marginBottom: '5px',
    fontFamily: MAIN_THEME.font.regular,
  },
  inputWrapper: {
    position: 'relative',
  },
  inputStyles: {
    width: '100%',
    border: `1px solid ${MAIN_THEME.color.white86}`,
    borderRadius: '5px',
    outline: 'none',
    '& .MuiInputBase-input': {
      fontFamily: MAIN_THEME.font.regular,
      '&::placeholder': {
        fontStyle: 'italic',
        fontFamily: MAIN_THEME.font.regular,
        color: MAIN_THEME.color.white86,
        opacity: 1,
        fontSize: MAIN_THEME.fontSize.sm,
      },
    },
    '& .MuiInputBase-root': {
      fontSize: MAIN_THEME.fontSize.sm,
      fontFamily: MAIN_THEME.font.regular,
      lineHeight: '15px',
      minHeight: '34px',
      width: '100%',
      padding: '0 25px 0 15px!important',
      margin: '0 auto',
      backgroundColor: MAIN_THEME.color.white98,
      outlineColor: error ? theme.palette.error.main : undefined,
      border: error
        ? `1px solid ${theme.palette.error.main}`
        : `0px solid ${MAIN_THEME.color.white92}`,
      boxSizing: 'border-box',
      borderRadius: '5px',
      '&::before': {
        display: 'none',
      },
      '&::after': {
        display: 'none',
      },
    },
  },
  chipMaxTagDesign: {
    fontSize: MAIN_THEME.fontSize.sm,
    fontFamily: MAIN_THEME.font.regular,
    height: 'unset',
    color: MAIN_THEME.color.black47,
    marginRight: '10px',
    backgroundColor: MAIN_THEME.color.white92,
    padding: '0 13px',
    borderRadius: '6px',
  },
  optionDesign: {
    fontSize: `${MAIN_THEME.fontSize.sm}`,
    lineHeight: '15px',
    color: MAIN_THEME.color.black,
    fontFamily: MAIN_THEME.font.regular,
  },
  inputLabelStyles: {
    marginBottom: '5px',
  },
  inputErrorStyles: {
    minHeight: '25px',
    color: MAIN_THEME.color.red,
    textAlign: 'left',
    fontSize: MAIN_THEME.fontSize.xs,
    marginTop: '5px',
  },
  inputRedBorder: {
    border: `1px solid ${MAIN_THEME.color.red}`,
  },
  chipStyle: {
    height: 'unset',
    color: MAIN_THEME.color.white,
    backgroundColor: MAIN_THEME.color.black,
    borderRadius: '0px',
    margin: '3px',
    '& .MuiChip-deleteIcon': {
      color: 'white !important',
    },
  },
  loader: {
    position: 'absolute',
    top: 'calc(50% - 10px)',
    right: '3%',
    opacity: 1,
    height: '25px',
    width: '25px',
    fontFamily: MAIN_THEME.font.regular,
  },
  selectMaterial: {
    '& .MuiInput-input': {
      fontFamily: MAIN_THEME.font.regular,
      fontSize: MAIN_THEME.fontSize.sm,
      lineHeight: '15px',
      color: MAIN_THEME.color.black25,
    },
    '& .MuiInputBase-root:before': {
      border: 'none',
      backgroundColor: 'unset',
      borderRadius: '0',
      borderBottom: `1px solid ${MAIN_THEME.color.white92}`,
    },

    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      {
        borderBottom: `1px solid ${MAIN_THEME.color.white92}`,
      },
  },
  selectError: {
    '& .MuiInputBase-root:before': {
      borderBottom: `1px solid ${theme.palette.error.main}`,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      {
        borderBottom: `1px solid ${theme.palette.error.main}`,
      },
    '& .MuiInputBase-root:focus': {
      borderColor: theme.palette.error.main,
      outlineColor: theme.palette.error.main,
      boxShadow: 'none',
      borderRadius: 0,
    },
    '& .MuiInputBase-root': {
      borderColor: `${theme.palette.error.main}`,
      outlineColor: `${theme.palette.error.main}`,
    },
  },
}));

const MultiselectFormField: React.FC<IMultiselectFormFieldProps> = ({
  name,
  title,
  hideError,
  placeholder,
  className,
  style,
  classes = {},
  values = [],
  error,
  onChange,
  onBlur = () => {},
  onFocus = () => {},
  disabled,
  selectOptions = {},
  inputOnly,
  loader,
  fullWidth,
  maxTagCount,
  freeSolo = false,
  onBlurSaveNewData = false,
  material,
  injectGlobalLov,
}) => {
  const { classes: inputClasses } = useStyles({ material, error });
  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    limit: undefined,
  });
  const lovs = useAppSelector((state) => state.lovs);

  const localSelectOptions: IAbstractRecord = useMemo(() => {
    if (injectGlobalLov) {
      const list: IAbstractRecord = {};
      const globalLov = lovs[injectGlobalLov.type];
      globalLov?.forEach((item) => {
        if (injectGlobalLov.display === 'Both') {
          list[item[injectGlobalLov.value]] = `${item.Symbol} - ${item.Title}`;
        } else {
          list[item[injectGlobalLov.value]] = item[injectGlobalLov.display];
        }
      });
      return list;
    }
    return selectOptions;
  }, [injectGlobalLov, lovs, selectOptions]);

  const renderInput = () => {
    return (
      <div className={inputClasses.inputContainerStyles}>
        <FormControl
          error={!!error}
          disabled={!!disabled}
          className={inputClasses.inputContainerStyles}
          size="small"
          variant="outlined"
        >
          <Autocomplete
            freeSolo={freeSolo}
            multiple={true}
            limitTags={maxTagCount}
            getLimitTagsText={(props) => (
              <div className={inputClasses.chipMaxTagDesign}>+{props}</div>
            )}
            disabled={disabled}
            disablePortal={false}
            options={Object.keys(localSelectOptions)}
            filterOptions={filterOptions}
            onChange={(event, newValue: string[]) => {
              onChange?.(newValue);
            }}
            onFocus={() => onFocus()}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
              if (onBlurSaveNewData) {
                onBlur(event.target.value as unknown as string[]);
              } else {
                onBlur([]);
              }
            }}
            clearOnBlur={onBlurSaveNewData}
            value={values || []}
            getOptionLabel={(option: string) => localSelectOptions[option]}
            disableClearable={false}
            selectOnFocus
            disableCloseOnSelect={true}
            renderOption={(props, option: string) => (
              <li {...props} key={option}>
                <span className={inputClasses.optionDesign}>
                  {localSelectOptions[option]}
                </span>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value?.map((option: string, index: number) => (
                <Chip
                  label={localSelectOptions[option]}
                  {...getTagProps({ index })}
                  className={inputClasses.chipStyle}
                  key={index}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                name={name}
                placeholder={values?.length > 0 ? '' : placeholder}
                className={clsx(
                  !material && inputClasses.inputStyles,
                  material && inputClasses.selectMaterial,
                  error && inputClasses.selectError
                )}
                variant="standard"
              />
            )}
          />
        </FormControl>
      </div>
    );
  };

  return inputOnly ? (
    renderInput()
  ) : (
    <>
      <FormFieldWrapper
        title={title}
        error={error}
        name={name}
        hideError={hideError}
        className={clsx(classes.wrapper, className)}
        fullWidth={fullWidth}
        material={material}
        style={style}
      >
        {renderInput()}
        {loader && (
          <CircularProgress
            className={inputClasses.loader}
            size={20}
            thickness={3}
          />
        )}
      </FormFieldWrapper>
    </>
  );
};

export default MultiselectFormField;
